import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "../../../store/store";
import ButtonLoader from "../../../atoms/ButtonLoader/ButtonLoader";
import { AssessmentMode, CareTriadKey } from "./AssessmentProgressContext";
import { ConfirmKind } from "./useCoachAssessment";
import { useCoachAssessment } from "./useCoachAssessment";
import QuestionField from "./QuestionField";
import { computeSpanFullIds } from "./questionLayout";
import LwaasPanel from "./LwaasPanel";
import { getSessionPdf } from "../../../services/rest/kairaAssessments";
import { isSectionSkipped, questionIsVisible } from "../SharedProfile/showIf";
import {
  AudienceBadge,
  IconGauge,
  sectionIcon,
  successSectionNumber,
} from "./sectionIcons";
import PageHeader from "../../../molecules/PatientDetailsV2/PageHeader/PageHeader";
import FieldPrevHistory from "./FieldPrevHistory";
import {
  formatOptionValue,
  prevVisitsForQuestion,
} from "./prevHistory";
import "./CoachAssessmentPage.scss";

const META: Record<
  CareTriadKey,
  { color: string; colorTint: string; role: string; eyebrow: string }
> = {
  diet: {
    color: "#299d6b",
    colorTint: "#e6f0e8",
    role: "Diet Coach · Nutritionist",
    eyebrow: "Diet Coach · Nutritionist",
  },
  exercise: {
    color: "#2e8e92",
    colorTint: "rgba(46, 142, 146, 0.1)",
    role: "Exercise Coach · Physiotherapist",
    eyebrow: "Exercise Coach · Physiotherapist",
  },
  success: {
    color: "#7d5a8c",
    colorTint: "#f3eef8",
    role: "Success Coach · CBT Therapist",
    eyebrow: "Success Coach · CBT Therapist",
  },
};

/** Trust section.audience only — visit type is which seed file loads. */
const displayAudience = (sec: any): "patient" | "coach" | "internal" => {
  if (sec?.audience === "internal") return "internal";
  if (sec?.audience === "patient") return "patient";
  return "coach";
};

const PUBLISHED_LOCK_TITLE = "This assessment is published and final.";
const PUBLISHED_LOCK_BODY =
  "It can't be edited or cancelled. Create a new follow-up to record changes.";

const AssessmentConfirm: React.FC<{
  title: string;
  copy: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ title, copy, onClose, children }) =>
  createPortal(
    <div className="coach-assessment">
      <div className="publish-confirm-overlay" onClick={onClose}>
        <div
          className="publish-confirm"
          role="dialog"
          aria-labelledby="publish-confirm-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="publish-confirm-title" className="publish-confirm-title">
            {title}
          </h3>
          <p className="publish-confirm-copy">{copy}</p>
          <div className="publish-confirm-actions">{children}</div>
        </div>
      </div>
    </div>,
    document.body,
  );

type Props = { view: CareTriadKey; mode?: AssessmentMode };

const CoachAssessmentPage: React.FC<Props> = ({
  view,
  mode = AssessmentMode.Followup,
}) => {
  const params = useParams();
  const patientId =
    useSelector(
      (s: RootState) => s.patient.patientDetails?.data?.patient_id,
    ) ||
    params?.patientId ||
    undefined;

  const meta = META[view];
  const {
    config,
    activeSessionId,
    answers,
    result,
    coachQuestions,
    loading,
    saving,
    saveNote,
    error,
    action,
    isPublished,
    source,
    canEdit,
    roleCanEdit,
    allSessions,
    setAnswer,
    saveNow,
    saveIfDirty,
    ensureSession,
    publish,
    cancelFollowUp,
    sectionAnswered,
    occurrence,
    overallProgress,
    canFollowUp,
    newFollowUp,
    confirmFollowUpCreate,
    closeConfirm,
    confirmKind,
  } = useCoachAssessment(view, patientId, mode);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [activeChip, setActiveChip] = useState<string | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportNote, setExportNote] = useState("");
  const [confirmPublish, setConfirmPublish] = useState(false);

  const sections = (config?.sections || []).filter(
    (section: any) => !section?.sharedProfile,
  );
  const questions = config?.questions || [];
  const isLwaas = config?.scoring?.type === "lwaas";
  const title = config?.coachTitle || config?.name || "Assessment";

  useEffect(() => {
    document.body.classList.add("assessment-view");
    if (isPublished || !roleCanEdit) document.body.classList.add("locked");
    else document.body.classList.remove("locked");
    return () => {
      document.body.classList.remove("assessment-view", "locked");
    };
  }, [isPublished, roleCanEdit]);

  useEffect(() => {
    const next: Record<string, boolean> = { coach_asks: true };
    if (config?.scoring?.type === "lwaas") next.lwaas_score = true;
    sections.forEach((section: any) => {
      if (section?.id) next[section.id] = true;
    });
    setOpenSections(next);
    const firstId = sections[0]?.id as string | undefined;
    if (firstId) setActiveChip(firstId);
  }, [config?.id, config?.scoring?.type, sections[0]?.id]);

  const canCancelDraft = action === "draft" && source === "coach";

  const canPublish =
    canEdit &&
    !saving &&
    overallProgress.requiredTotal > 0 &&
    overallProgress.requiredAnswered >= overallProgress.requiredTotal;

  const questionsBySection = useMemo(() => {
    const map: Record<string, any[]> = {};
    questions.forEach((q: any) => {
      if (!questionIsVisible(q, answers)) return;
      if (!map[q.sectionId]) map[q.sectionId] = [];
      map[q.sectionId].push(q);
    });
    return map;
  }, [answers, questions]);

  const sessionDone = action === "preview" || action === "published";

  const sectionSkipped = (sec: any, qs: any[]) =>
    isSectionSkipped(qs, answers, {
      sectionSkippable:
        sec?.skippable === true ||
        (qs.length > 0 && qs.every((q: any) => q.required !== true)),
      sessionDone,
      patientFill: displayAudience(sec) === "patient",
    });

  const exportPdf = async () => {
    if (!patientId || !activeSessionId || !isPublished || exportingPdf) return;
    setExportingPdf(true);
    setExportNote("");
    try {
      const data = await getSessionPdf(activeSessionId, patientId);
      const url = data?.url;
      if (url) window.open(url, "_blank", "noopener,noreferrer");
      else setExportNote("Export PDF failed");
    } catch (e: any) {
      setExportNote(e?.message || "Export PDF failed");
    } finally {
      setExportingPdf(false);
    }
  };

  const confirmUi =
    confirmKind === ConfirmKind.BaselineBlock ? (
      <AssessmentConfirm
        title="Cannot create a baseline"
        copy="This user is an existing user and has already had an appointment. Please book a follow-up for this patient."
        onClose={closeConfirm}
      >
        <button
          type="button"
          className="btn btn-dark btn-publish"
          onClick={closeConfirm}
        >
          OK
        </button>
      </AssessmentConfirm>
    ) : confirmKind === ConfirmKind.FollowupWarn ? (
      <AssessmentConfirm
        title="Baseline not completed"
        copy="This patient has not completed their baseline assessment. Do you still want to proceed with follow-up creation?"
        onClose={closeConfirm}
      >
        <button
          type="button"
          className="btn btn-export"
          onClick={closeConfirm}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-dark btn-publish"
          disabled={saving}
          onClick={() => void confirmFollowUpCreate()}
        >
          Yes
        </button>
      </AssessmentConfirm>
    ) : confirmPublish ? (
      <AssessmentConfirm
        title="Publish this assessment?"
        copy="The patient will be notified. You will not be able to edit answers after publishing."
        onClose={() => setConfirmPublish(false)}
      >
        <button
          type="button"
          className="btn btn-export"
          onClick={() => setConfirmPublish(false)}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-dark btn-publish"
          disabled={saving || !canPublish}
          onClick={() => {
            setConfirmPublish(false);
            void publish();
          }}
        >
          Publish
        </button>
      </AssessmentConfirm>
    ) : null;

  if (loading) {
    return (
      <div className="coach-assessment">
        <PageHeader eyebrow={meta.eyebrow} title={title} />
        <div className="ca-empty">Loading assessment…</div>
      </div>
    );
  }

  if (error && !config) {
    return (
      <div className="coach-assessment">
        <PageHeader eyebrow={meta.eyebrow} title={title} />
        <div className="ca-empty">{error}</div>
      </div>
    );
  }

  if (!activeSessionId) {
    return (
      <div
        className={`coach-assessment coach-assessment--${view}`}
        style={
          {
            ["--coach" as any]: meta.color,
            ["--coach-50" as any]: meta.colorTint,
          } as React.CSSProperties
        }
      >
        <PageHeader eyebrow={meta.eyebrow} title={title} />
        <div className="ca-empty">
          <p>
            {mode === AssessmentMode.Followup
              ? "No follow-up session yet for this patient."
              : "No assessment session yet for this patient."}
          </p>
          {roleCanEdit && mode === AssessmentMode.Baseline && (
            <button
              type="button"
              className="btn btn-solid"
              disabled={saving}
              onClick={() => ensureSession()}
            >
              Start assessment
            </button>
          )}
          {roleCanEdit && mode === AssessmentMode.Followup && canFollowUp && (
            <button
              type="button"
              className="btn btn-solid"
              disabled={saving}
              onClick={() => newFollowUp()}
            >
              New follow-up
            </button>
          )}
          {roleCanEdit && mode === AssessmentMode.Followup && !canFollowUp && (
            <p>Use New follow-up in the header once the latest visit is published.</p>
          )}
        </div>
        {confirmUi}
      </div>
    );
  }

  return (
    <div
      className={`coach-assessment coach-assessment--${view}`}
      style={
        {
          ["--coach" as any]: meta.color,
          ["--coach-50" as any]: meta.colorTint,
        } as React.CSSProperties
      }
    >
      {isPublished && (
        <div className="locked-banner" style={{ display: "flex" }}>
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 15v2M5 11V7a7 7 0 0 1 14 0v4" />
            <rect x="4" y="11" width="16" height="10" rx="2" />
          </svg>
          <span>
            <b>{PUBLISHED_LOCK_TITLE}</b> {PUBLISHED_LOCK_BODY}
          </span>
        </div>
      )}

      <PageHeader eyebrow={meta.eyebrow} title={title} />
      {occurrence ? (
        <div className="page-sub">
          {occurrence}
          {overallProgress.total > 0
            ? ` · ${overallProgress.answered}/${overallProgress.total} answered`
            : null}
        </div>
      ) : null}

      <div className="subnav">
        {coachQuestions.length > 0 && (
          <button
            type="button"
            className={`subnav-chip ${
              activeChip === "coach_asks" ? "is-on" : ""
            }`}
            onClick={() => {
              setActiveChip("coach_asks");
              setOpenSections((p) => ({ ...p, coach_asks: true }));
              document
                .getElementById("sec-coach-asks")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
              void saveIfDirty();
            }}
          >
            Questions for coach
          </button>
        )}
        {sections.map((section: any) => {
          const sectionQs = questionsBySection[section.id] || [];
          if (!sectionQs.length) return null;
          const skipped = sectionSkipped(section, sectionQs);
          return (
            <button
              key={section.id}
              type="button"
              className={`subnav-chip ${activeChip === section.id ? "is-on" : ""} ${
                skipped ? "is-skipped" : ""
              }`}
              onClick={() => {
                setActiveChip(section.id);
                setOpenSections((p) => ({ ...p, [section.id]: true }));
                document
                  .getElementById(`sec-${section.id}`)
                  ?.scrollIntoView({ behavior: "smooth", block: "start" });
                void saveIfDirty();
              }}
            >
              {section.name}
              {skipped ? <span className="subnav-skip-dot" title="Skipped" /> : null}
            </button>
          );
        })}
      </div>

      {coachQuestions.length > 0 && (
        <div
          className={`section ask-coach-section ${
            openSections.coach_asks === false ? "collapsed" : ""
          }`}
          id="sec-coach-asks"
        >
          <div
            className="section-head"
            onClick={() => {
              setOpenSections((p) => ({
                ...p,
                coach_asks: p.coach_asks === false,
              }));
              void saveIfDirty();
            }}
          >
            <div
              className="section-ico"
              style={{ background: "#F8F1E8", color: "#8a4b1a" }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                width="18"
                height="18"
              >
                <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z" />
              </svg>
            </div>
            <div className="section-titles">
              <div className="section-title">Questions for the coach</div>
              <div className="section-meta">
                Asked by the patient during this check-in
              </div>
            </div>
            <span className="count-pill">
              {coachQuestions.length}{" "}
              {coachQuestions.length === 1 ? "question" : "questions"}
            </span>
            <svg
              className="chev"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div className="section-body">
            <ul className="ask-coach-list">
              {coachQuestions.map((q, idx) => (
                <li key={`${idx}-${q}`}>{q}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {isLwaas && (
        <div
          className={`section ${
            openSections.lwaas_score === false ? "collapsed" : ""
          }`}
          id="sec-lwaas-score"
        >
          <div
            className="section-head"
            onClick={() => {
              setOpenSections((p) => ({
                ...p,
                lwaas_score: p.lwaas_score === false,
              }));
              void saveIfDirty();
            }}
          >
            <div
              className="section-ico"
              style={{ background: "#F3EEF8", color: "var(--success)" }}
            >
              <IconGauge />
            </div>
            <div className="section-titles">
              <div className="section-title">LWAAS v2.0 · Live Score</div>
              <div className="section-meta">
                Reverse-scored & banded as you answer
              </div>
            </div>
            <span className="count-pill">
              {result?.lwaas?.answered || 0} / {config?.scoring?.itemCount || 25}{" "}
              items
            </span>
            <svg
              className="chev"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
          <div className="section-body">
            <LwaasPanel scoring={config.scoring} result={result} />
          </div>
        </div>
      )}

      {sections.map((sec: any) => {
        const rawQs = questionsBySection[sec.id] || [];
        const skipped = sectionSkipped(sec, rawQs);
        const qs = rawQs;
        if (!qs.length) return null;
        const { answered, total } = sectionAnswered(sec.id);
        const collapsed = openSections[sec.id] === false;
        const aud = displayAudience(sec);
        const sectionOptional =
          sec.skippable === true ||
          (rawQs.length > 0 && rawQs.every((q: any) => q.required !== true));
        const spanFull = computeSpanFullIds(qs);
        const successNum =
          view === "success" ? successSectionNumber(sec.id) : null;
        return (
          <div
            key={sec.id}
            className={`section ${collapsed ? "collapsed" : ""} ${
              skipped ? "is-skipped" : ""
            }`}
            id={`sec-${sec.id}`}
            data-aud={aud}
          >
            <div
              className="section-head"
              onClick={() => {
                setOpenSections((p) => ({
                  ...p,
                  [sec.id]: p[sec.id] === false,
                }));
                void saveIfDirty();
              }}
            >
              <div className="section-ico">
                {successNum != null ? (
                  <div className="domain-num">{successNum}</div>
                ) : (
                  sectionIcon(sec.id, sec.icon)
                )}
              </div>
              <div className="section-titles">
                <div className="section-title">{sec.name}</div>
                <div className="section-meta">{sec.blurb || ""}</div>
              </div>
              <div className="section-pills">
                {skipped ? (
                  <span
                    className="skip-pill"
                    title="Patient skipped this section"
                  >
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      width="12"
                      height="12"
                      aria-hidden
                    >
                      <circle cx="12" cy="12" r="9" />
                      <path d="M8 12h8" />
                    </svg>
                    Skipped
                  </span>
                ) : (
                  <span
                    className={`req-badge ${
                      sectionOptional ? "req-optional" : "req-required"
                    }`}
                  >
                    {sectionOptional ? "Optional" : "Required"}
                  </span>
                )}
                <AudienceBadge audience={aud} />
                <span
                  className={`count-pill ${
                    total > 0 && answered === total ? "done" : ""
                  }`}
                >
                  {answered}/{total} answered
                </span>
              </div>
              <svg
                className="chev"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </div>
            <div className="section-body">
              <div className="grid cols-2">
                {qs.map((q: any) => (
                  <div
                    key={q.id}
                    className={`field ${
                      spanFull.has(String(q.id))
                        ? "span-full"
                        : ""
                    }`}
                  >
                    <div className="label">
                      {q.prompt}
                      {/* Per-question Required/Optional — keep section-level only for now
                      <span
                        className={`req-badge ${
                          q.required === true ? "req-required" : "req-optional"
                        }`}
                      >
                        {q.required === true ? "Required" : "Optional"}
                      </span>
                      */}
                      {q.reverse ? (
                        <span
                          className="rev"
                          title="Agreement on this item lowers the readiness score (reverse-scored)"
                        >
                          Reverse scored
                        </span>
                      ) : null}
                    </div>
                    <FieldPrevHistory
                      label={q.prompt || q.id}
                      visits={prevVisitsForQuestion(
                        allSessions,
                        activeSessionId,
                        q.id,
                      )}
                      formatValue={(v) => formatOptionValue(q, v)}
                    />
                    {(q.helper || q.help) && (
                      <div className="helper">{q.helper || q.help}</div>
                    )}
                    <QuestionField
                      question={q}
                      value={answers[q.id]}
                      disabled={!canEdit}
                      answersOnly={isPublished}
                      scales={config?.scales}
                      onChange={(v) => setAnswer(q.id, v)}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}

      <div className="actionbar">
        <span className="save-note">
          {saving
            ? "Saving…"
            : exportNote || saveNote || "All changes saved"}
        </span>
        <span className="spacer" />
        {canCancelDraft && roleCanEdit && (
          <button
            type="button"
            className="btn btn-danger-ghost"
            disabled={saving}
            onClick={() => cancelFollowUp()}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="16"
              height="16"
              aria-hidden
            >
              <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            </svg>
            {mode === AssessmentMode.Baseline ? "Cancel baseline" : "Cancel follow-up"}
          </button>
        )}
        {roleCanEdit && (
          <button
            type="button"
            className="btn btn-solid"
            disabled={!canEdit || saving}
            onClick={() => saveNow()}
          >
            {saving ? (
              <ButtonLoader />
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                width="16"
                height="16"
                aria-hidden
              >
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                <path d="M17 21v-8H7v8M7 3v5h8" />
              </svg>
            )}
            {saving ? "Saving…" : "Save"}
          </button>
        )}
        <button
          type="button"
          className="btn btn-export"
          disabled={!isPublished || exportingPdf || saving}
          title={
            isPublished
              ? "Download assessment PDF"
              : "Publish assessment to enable Export PDF"
          }
          onClick={() => exportPdf()}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            width="16"
            height="16"
            aria-hidden
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
          </svg>
          {exportingPdf ? "Exporting…" : "Export PDF"}
        </button>
        {roleCanEdit && (
          <button
            type="button"
            className="btn btn-dark btn-publish"
            disabled={isPublished || !canPublish}
            title={
              isPublished
                ? "Assessment is already published"
                : canPublish
                  ? "Publish this assessment"
                  : overallProgress.requiredTotal > 0
                    ? `Answer required questions to publish (${overallProgress.requiredAnswered}/${overallProgress.requiredTotal})`
                    : "Nothing to publish yet"
            }
            onClick={() => setConfirmPublish(true)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              width="16"
              height="16"
              aria-hidden
            >
              <path d="M22 2 11 13M22 2l-7 20-4-9-9-4z" />
            </svg>
            {isPublished ? "Assessment Published" : "Publish Assessment"}
          </button>
        )}
      </div>

      {confirmUi}
    </div>
  );
};

export default CoachAssessmentPage;
